// apps/client/src/ui/screens/letter.tsx (M1-T17) — one letter. Opening it sends `mail.open` (marks it read; the ack carries the full
// letter). DESIGN.md letter card: the from-line with the sender tag, an 800-weight body on faint ruled lines, a 900 signature, a
// cream-200 attachment pill, tilted 1.2deg. A wrapped gift shows as a box until unwrapped; the letter can't be thrown away while the
// gift is still in it. M2-T08: a letter from a resident has a quiet Report letter button.
import { getEntry, t as tr, type StringKey } from '@whiskerton/content';
import type { MailFull, MailId } from '@whiskerton/shared';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useCommands } from '../../game/instance';
import { useGame } from '../../state/store';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Button, Panel } from '../kit';
import type { ScreenDef } from '../router';
import { Sender } from './mailbox';

declare module '../router' { interface ScreenParams { letter: { mail: MailId } } }

/** What the letter says: a resident's (already masked) body, or a system/villager template filled with your name. */
export function letterText(m: MailFull, yourName: string): string {
  if (m.body !== null) return m.body;
  return m.template ? tr(m.template.key as StringKey, { ...m.template.params, name: yourName }) : '';
}
const signature = (m: MailFull): string =>
  m.from.kind === 'system' ? tr(`mail.from${m.from.key[0]!.toUpperCase()}${m.from.key.slice(1)}` as StringKey)
    : m.from.kind === 'resident' && m.from.id === null ? tr('common.formerResident') : m.from.name;

/** Villager and curator letters sign off in their own text (M2 playtest: 'Ziggy' twice); the card's signature then stays off. */
const signedInBody = (text: string, sig: string): boolean => text.trimEnd().split('\n').at(-1)?.trim() === sig.trim();

function Attachment({ m, take }: { m: MailFull; take: () => void }) {
  const t = useT();
  const [unwrapped, setUnwrapped] = useState(!m.wrapped);
  const icon = useIcon(unwrapped && m.item ? m.item.catalogId : null);
  if (!m.item) return null;
  const entry = getEntry(m.item.catalogId);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--cream-200)', borderRadius: 999, padding: '6px 8px 6px 14px', justifySelf: 'start' }}>
      {unwrapped ? (
        <>
          <img src={icon} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800 }}>{entry ? itemName(entry.name) : m.item.catalogId}{m.item.qty > 1 && ` ${t('items.qty', { n: m.item.qty })}`}</span>
          <Button onClick={take}>{t('mail.take')}</Button>
        </>
      ) : (
        <>
          <span aria-hidden style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--coral-500)', boxShadow: 'inset 0 0 0 4px var(--sun-400)' }} />
          <span style={{ fontWeight: 800 }}>{t('mail.wrapped')}</span>
          <Button onClick={() => setUnwrapped(true)}>{t('mail.open')}</Button>
        </>
      )}
    </div>
  );
}

function Letter({ mail, close }: { mail: MailId; close: () => void }) {
  const t = useT();
  const cmd = useCommands();
  const { you, residents, openScreen, townId } = useGame(useShallow((s) => ({ you: s.you, residents: s.residents, openScreen: s.openScreen, townId: s.townId })));
  const [full, setFull] = useState<MailFull | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    void cmd.act('mail.open', { mail }).then((res) => { if (live && res.ok) setFull(res.r.mail); });
    return () => { live = false; };
  }, [mail]);   // cmd is the game's one command API; open once per letter
  if (!full) return <Panel title={t('mail.title')}><p style={{ margin: 0, width: 'min(480px, 70cqw)' }}>…</p></Panel>;

  const yourName = you ? (residents[you.resident]?.name ?? '') : '';
  const take = async () => {
    setBusy(true);
    try { const res = await cmd.act('mail.take', { mail }); if (res.ok) setFull({ ...full, item: null, hasItem: false }); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { const res = await cmd.act('mail.delete', { mail }); if (res.ok) close(); } finally { setBusy(false); }
  };
  const replyTo = full.from.kind === 'resident' && full.from.id && residents[full.from.id] ? full.from.id : null;
  return (
    <Panel title={t('mail.title')}>
      <div style={{ display: 'grid', gap: 14, width: 'min(520px, 72cqw)', maxWidth: '100%' }}>
        <article className="letter-card" style={{ display: 'grid', gap: 10, padding: '16px 20px', borderRadius: 18, background: 'var(--cream-50)', boxShadow: 'var(--sh-soft)',
          transform: 'rotate(1.2deg)', margin: '6px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Sender from={full.from} /></div>
          <p style={{ margin: 0, fontWeight: 800, lineHeight: '1.45em', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', userSelect: 'text',
            backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0, transparent calc(1.45em - 1px), rgba(176,138,99,.28) calc(1.45em - 1px), rgba(176,138,99,.28) 1.45em)' }}>
            {letterText(full, yourName)}
          </p>
          {!signedInBody(letterText(full, yourName), signature(full)) && <p data-testid="letter-signature" style={{ margin: 0, fontWeight: 900, justifySelf: 'end' }}>{signature(full)}</p>}
          <Attachment key={full.item?.id ?? 'none'} m={full} take={() => void take()} />
        </article>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {replyTo && <Button onClick={() => openScreen('writer', { to: replyTo })}>{t('mail.reply')}</Button>}
          <Button variant="quiet" disabled={busy || full.item !== null} onClick={() => void remove()}>{t('mail.delete')}</Button>
          {full.item !== null && <span style={{ fontSize: 14 }}>{t('mail.keepFirst')}</span>}
          {townId && full.from.kind === 'resident' && full.from.id !== null && (
            <Button variant="quiet" onClick={() => openScreen('report', { townId, kind: 'letter', refId: full.id, preview: full.body ?? '' })}>{t('safety.reportLetter')}</Button>
          )}
        </div>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'letter'> = { id: 'letter', layer: 'panel', component: ({ params, close }) => <Letter mail={params.mail} close={close} /> };
